/**
 * Редактор цвета: оттенок, вид миниатюры и цвет блёсток.
 *
 * Оттенок задаётся тремя способами сразу — палитрой системы, HEX и RGB.
 * Артикулы производителя приходят с описанием в HEX, а подбирать похожий
 * оттенок «на глаз» удобнее ползунками RGB; заставлять выбирать один способ
 * незачем, все три правят одно значение.
 */

import { useEffect, useState } from 'react';
import { hexToRgb, normalizeHex, rgbToHex } from '@otc/shared';
import { Check } from 'lucide-react';
import { FILL_LABELS, Swatch, type ColorFill } from './Swatch.js';
import { usePopover } from './usePopover.js';

export interface EditableColor {
  id: string;
  name: string;
  hex: string;
  fill: ColorFill;
  glitterHex: string;
}

/*
 * Разбор и сборка кода цвета — общие с «Настройки → Цвета» и сервером.
 * Отдаются и отсюда: этим редактором их привыкли брать.
 */
export { hexToRgb, normalizeHex, rgbToHex };

const FILLS: ColorFill[] = ['SOLID', 'GLITTER', 'IRIS', 'RAINBOW'];

/**
 * Образец цвета, открывающий редактор по клику.
 *
 * Миниатюра здесь ОДНА и она же кнопка: отдельный образец «для просмотра»
 * рядом с образцом «для правки» показывал одно и то же дважды.
 *
 * Правки копятся в черновике окна и применяются по «ОК». Живое применение
 * означало бы, что случайно сдвинутый ползунок уже изменил цвет и раздел
 * помечен изменённым — откатить можно только вспомнив прежний оттенок.
 */
export function ColorEditor({
  color,
  size = 22,
  onChange,
}: {
  color: EditableColor;
  size?: number;
  onChange: (patch: Partial<EditableColor>) => void;
}) {
  const popover = usePopover({ width: 280, height: 380 });
  const [draft, setDraft] = useState<EditableColor>(color);

  const start = (element: HTMLElement) => {
    setDraft(color);
    popover.openAt(element);
  };

  const confirm = () => {
    onChange({ hex: draft.hex, fill: draft.fill, glitterHex: draft.glitterHex });
    popover.close();
  };

  const changed =
    draft.hex !== color.hex || draft.fill !== color.fill || draft.glitterHex !== color.glitterHex;

  return (
    <>
      <button
        onClick={(event) => (popover.open ? popover.close() : start(event.currentTarget))}
        className="flex-shrink-0 rounded hover:opacity-80"
        title="Изменить цвет и вид миниатюры"
      >
        <Swatch color={color} size={size} />
      </button>

      {/* Прокрутка внутри: предельную высоту теперь задаёт сам механизм
          по месту в окне, и на низком экране содержимое иначе обрезалось бы. */}
      {popover.open && (
        <div
          ref={popover.popoverRef}
          className="overflow-y-auto rounded-lg border p-3 shadow-lg"
          style={popover.style}
        >
          <ColorControls
            color={draft}
            onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
          />

          <div className="mt-3 flex gap-2 border-t pt-2" style={{ borderColor: 'var(--otc-line)' }}>
            <button
              onClick={confirm}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-sm text-white disabled:opacity-40"
              style={{ background: 'var(--otc-accent)' }}
              disabled={!changed}
            >
              <Check size={13} /> ОК
            </button>
            <button
              onClick={popover.close}
              className="rounded-lg border px-3 py-1.5 text-sm text-ink2"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Тело редактора — вынесено, чтобы переиспользоваться в окне добавления цвета. */
export function ColorControls({
  color,
  onChange,
}: {
  color: EditableColor;
  onChange: (patch: Partial<EditableColor>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Swatch color={color} size={44} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-ink">{color.name}</p>
          <p className="text-[11px] text-ink2">{FILL_LABELS[color.fill]}</p>
        </div>
      </div>

      {/* Вид миниатюры */}
      <div>
        <p className="mb-1 text-[11px] font-medium text-ink2">Вид миниатюры</p>
        <div className="flex gap-1">
          {FILLS.map((fill) => (
            <button
              key={fill}
              onClick={() => onChange({ fill })}
              className="flex flex-1 flex-col items-center gap-1 rounded-md border py-1.5"
              style={{
                borderColor: color.fill === fill ? 'var(--otc-accent)' : 'var(--otc-line)',
                borderWidth: color.fill === fill ? 2 : 1,
              }}
              title={FILL_LABELS[fill]}
            >
              <Swatch color={{ ...color, fill }} size={22} />
              <span className="text-[10px] text-ink2">
                {fill === 'SOLID'
                  ? 'Заливка'
                  : fill === 'GLITTER'
                    ? 'Блёстки'
                    : fill === 'IRIS'
                      ? 'Ирис'
                      : 'Радуга'}
              </span>
            </button>
          ))}
        </div>
      </div>

      <ChannelPicker
        label={color.fill === 'SOLID' ? 'Цвет' : 'Цвет основы'}
        value={color.hex}
        onChange={(hex) => onChange({ hex })}
      />

      {/* Цвет крапин нужен только обычным блёсткам: у «Ириса» своя палитра. */}
      {color.fill === 'GLITTER' && (
        <ChannelPicker
          label="Цвет блёсток"
          value={color.glitterHex}
          onChange={(glitterHex) => onChange({ glitterHex })}
        />
      )}

      {color.fill === 'IRIS' && (
        <p className="text-[11px] text-ink2">
          У «Ириса» крапины разноцветные — отдельный цвет блёсток не задаётся.
        </p>
      )}
    </div>
  );
}

/** Подбор оттенка: палитра системы, HEX и три канала RGB — одно значение. */
function ChannelPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  const [hexDraft, setHexDraft] = useState(value);
  const rgb = hexToRgb(value);

  // Внешнее изменение (например, выбор палитрой) обновляет поле ввода.
  useEffect(() => setHexDraft(value), [value]);

  const commitHex = (input: string) => {
    setHexDraft(input);
    const normalized = normalizeHex(input);
    if (normalized) onChange(normalized);
  };

  const setChannel = (channel: 'r' | 'g' | 'b', raw: number) =>
    onChange(rgbToHex(
      channel === 'r' ? raw : rgb.r,
      channel === 'g' ? raw : rgb.g,
      channel === 'b' ? raw : rgb.b,
    ));

  return (
    <div>
      <p className="mb-1 text-[11px] font-medium text-ink2">{label}</p>

      <div className="mb-1.5 flex items-center gap-2">
        <input
          type="color"
          value={/^#[\da-f]{6}$/i.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-7 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
          title="Палитра"
        />
        <input
          className="tabular w-24 rounded border px-1.5 py-1 text-xs uppercase"
          style={{
            // Незавершённый ввод подсвечен: пользователь видит, что значение
            // ещё не применилось, а не гадает, почему цвет не поменялся.
            borderColor: normalizeHex(hexDraft) ? 'var(--otc-line)' : 'var(--otc-coral)',
          }}
          value={hexDraft}
          onChange={(e) => commitHex(e.target.value)}
          placeholder="#RRGGBB"
          maxLength={7}
        />
      </div>

      {(['r', 'g', 'b'] as const).map((channel) => (
        <div key={channel} className="mb-1 flex items-center gap-2">
          <span className="w-3 text-[11px] uppercase text-ink2">{channel}</span>
          <input
            type="range"
            min={0}
            max={255}
            value={rgb[channel]}
            onChange={(e) => setChannel(channel, Number(e.target.value))}
            className="h-1 flex-1 cursor-pointer"
          />
          <input
            type="number"
            min={0}
            max={255}
            value={rgb[channel]}
            onChange={(e) => setChannel(channel, Number(e.target.value))}
            className="tabular w-14 rounded border px-1 py-0.5 text-right text-xs"
            style={{ borderColor: 'var(--otc-line)' }}
          />
        </div>
      ))}
    </div>
  );
}
